<template>
  <div class="form-field-container" :class="[classes.fieldContainer]">
    <div
      class="form-field"
      :class="[grayOut && 'grayout', error && 'error', resetStyles && 'reset', classes.field]"
      @click="show = true"
      ref="field"
    >
      <div class="flex items-center justify-between w-full h-full">
        <div :class="[labelPosition && `form-label-${labelPosition}`]">{{ label }}</div>

        <!-- single selection mode -->
        <div
          v-if="!multiselect"
          class="form-select"
          :class="[isEmpty && 'empty', resetStyles && 'reset']"
        >
          <div v-if="selectedOption">
            {{ formatSelection(pluckOption(selectedOption)) }}
          </div>
          <div v-else class="form-select-placeholder">
            {{ placeholder }}
          </div>
        </div>

        <!-- show on multiselect mode -->
        <div
          v-if="multiselect"
          class="form-select"
          :class="[isEmpty && 'empty', resetStyles && 'reset']"
        >
          <div v-if="selectedOptions.length" class="w-full">
            <ul class="flex flex-wrap py-2 pt-4">
              <li
                class="max-w-full py-1"
                v-for="(opt, index) in selectedOptions"
                :key="`multiselect-option-${index}`"
              >
                <div class="px-3 py-1 text-white truncate rounded-full bg-ricoma-gray-700">
                  {{ formatSelection(pluckOption(opt)) }}
                </div>
              </li>
            </ul>
          </div>
          <div class="form-select-placeholder" v-else>{{ placeholder }}</div>
        </div>

        <span class="flex-shrink-0 w-12 form-field-icon right" :class="[resetStyles && 'reset']">
          <VSvgIcon name="menu-down" />
        </span>
      </div>

      <!-- list of select options -->
      <div v-show="show" class="form-select-options" :class="[classes.options]">
        <div ref="filter">
          <input
            v-show="showFilterInput"
            type="text"
            placeholder="search..."
            v-model="filter"
            class="w-full px-3 py-4 border-b outline-none border-ricoma-gray-700 bg-ricoma-gray-200 placeholder-ricoma-gray-700"
          />
        </div>

        <div
          v-if="options && !options.length"
          class="flex items-center justify-center h-full px-4 py-4 text-ricoma-gray-700"
        >
          -- Empty --
        </div>

        <ul v-else class="h-full overflow-y-auto">
          <li
            v-for="(option, index) in options"
            :key="`option-${index}`"
            class="form-select-options-option"
            :class="[isSelected(option) && 'bg-ricoma-gray-300']"
            @click.stop="handleSelect(option)"
          >
            <div v-if="asHtml" v-html="formatOptions(pluckOption(option))"></div>
            <div v-else>{{ formatOptions(pluckOption(option)) }}</div>
          </li>
        </ul>
      </div>
    </div>
    <div v-if="error" class="form-error">{{ error }}</div>
  </div>
</template>

<script>
import VSvgIcon from './VSvgIcon';

import { typeCheck, debounce } from '@/utils';

export default {
  name: 'VFormSelect',
  components: { VSvgIcon },
  data: function () {
    return {
      show: false,
      selectedOption: null,
      selectedOptions: [],
      filter: '',
    };
  },
  props: {
    label: String,
    placeholder: String,
    // can be Array<Primitives> || Array<Object>
    options: {
      type: Array,
      required: false,
      default: () => [],
    },
    // if options is Array<Object> we need to pass key to access desired value
    optionKey: {
      type: String,
      required: false,
    },
    // We can select mutilple values and return new Array<Object>.
    // In such case we need to pass fn formatOptions prop in order manipulate the presentation value
    optionKeys: {
      type: Array,
      required: false,
    },
    // fn (any) :: any
    // We can use this prop to modify the presentation value. Usefull when option is of type Object
    formatOptions: {
      type: Function,
      required: false,
      default: option => option,
    },
    // fn (any) :: any
    // just like fn formatOptions prop, it modifies the selected option presentation value
    formatSelection: {
      type: Function,
      required: false,
      default: selection => selection,
    },
    // Use in the case we want to escape HTML
    asHtml: {
      type: Boolean,
      required: false,
      default: false,
    },
    // default value for single select mode (default)
    initialSelectedOption: {
      type: [String, Object],
      required: false,
    },
    // default values for multiselect mode
    initialSelectedOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    multiselect: {
      type: Boolean,
      required: false,
    },
    error: {
      type: String,
      required: false,
    },
    fullDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    resetStyles: {
      type: Boolean,
      required: false,
      default: false,
    },
    reset: {
      type: Boolean,
      required: false,
      default: false,
    },
    labelPosition: {
      type: String,
      required: false,
      default: 'top',
    },
    classes: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    showFilterInput: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  watch: {
    filter: debounce(async function (text) {
      this.$emit('filter', text);
    }, 500),

    options(opts) {
      // reset selection when options array is empty
      if (!opts.length) {
        !opts.length && (this.selectedOption = null);
      }
    },
    initialSelectedOption(option) {
      this.selectedOption = option;
    },
    initialSelectedOptions(options) {
      this.selectedOption = options;
    },
    selectedOption(option) {
      this.$emit('onSelect', option);
    },
    selectedOptions(options) {
      this.$emit('onSelect', options);
    },
    reset(val) {
      if (val && this.multiselect) this.selectedOptions = [];
      else if (val) this.selectedOption = null;
    },
  },
  computed: {
    isEmpty() {
      return !this.selectedOption;
    },
    grayOut() {
      return this.fullDisabled && this.isEmpty && !this.options.length;
    },
  },
  methods: {
    handleSelect(option) {
      // single selection
      if (!this.multiselect) {
        this.selectedOption = option;
        return (this.show = false);
      }

      // handle multiselection
      const options = this.selectedOptions.some(opt => opt === option)
        ? this.selectedOptions.filter(opt => opt !== option)
        : [...this.selectedOptions, option];

      this.selectedOptions = options;
      this.show = false;
    },
    handleShow() {
      this.show = !this.show;
    },
    handleClickOutside(e) {
      const fieldElement = this.$refs.field;
      const filterElement = this.$refs.filter;

      // don't close modal when click filter input
      if (filterElement === e.target || filterElement.contains(e.target)) return;

      if (!(fieldElement === e.target || fieldElement.contains(e.target))) {
        this.show && (this.show = false);
      }
    },
    isSelected(option) {
      return this.selectedOptions.some(opt => opt === option);
    },
    // pluck value or values from object we want to present in the options list else show option when it's a primitive
    pluckOption(option) {
      if (this.optionKey && typeCheck(option).isObj) return option[this.optionKey];
      if (this.optionKeys && this.optionKeys.length && typeCheck(option).isObj)
        return this.optionKeys.reduce((acc, curr) => ((acc[curr] = option[curr]), acc), {});
      return option;
    },
  },
  mounted() {
    !this.multiselect
      ? this.initialSelectedOption && (this.selectedOption = this.initialSelectedOption)
      : this.initialSelectedOptions.length && (this.selectedOptions = this.initialSelectedOptions);

    document.addEventListener('click', this.handleClickOutside);
  },
  destroyed() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>
